import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, FaTimes, FaBookOpen, FaLayerGroup, FaCheck, 
  FaThLarge, FaList, FaQuoteLeft, FaCopy, FaCalendarAlt, 
  FaUserEdit, FaChartPie, FaBookmark, FaPause, FaClock
} from 'react-icons/fa';
import books from '../data/books';

function normalizeSessions(sessions, totalPages) {
  if (!Array.isArray(sessions)) return [];
  const cleaned = sessions
    .map(entry => {
      let from = Number(entry.from);
      let to = Number(entry.to);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
      if (from > to) [from, to] = [to, from];
      from = Math.max(1, Math.min(from, totalPages || Infinity));
      to = Math.max(1, Math.min(to, totalPages || Infinity));
      return { from, to };
    })
    .filter(Boolean);
  return cleaned;
}

function mergeIntervals(intervals) {
  if (intervals.length === 0) return [];
  const sortedByStart = intervals.slice().sort((a, b) => a.from - b.from || a.to - b.to);
  const merged = [];
  for (const current of sortedByStart) {
    if (merged.length === 0) {
      merged.push({ ...current });
      continue;
    }
    const last = merged[merged.length - 1];
    if (current.from <= last.to + 1) {
      last.to = Math.max(last.to, current.to);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

function getBookStats(book) {
  const totalPages = Number(book.totalPages) || 0;
  const sessions = normalizeSessions(book.sessions || [], totalPages);
  const merged = mergeIntervals(sessions);
  const uniquePagesRead = merged.reduce((sum, range) => sum + (range.to - range.from + 1), 0);
  const percent = totalPages > 0 ? Math.min(100, Math.round((uniquePagesRead / totalPages) * 100)) : 0;
  const currentPage = sessions.length > 0 ? Math.max(...sessions.map(s => s.to)) : 0;
  return { totalPages, sessions, merged, uniquePagesRead, percent, currentPage };
}

function parsePublicationDate(input) {
  if (!input) return null;
  const normalized = String(input).trim();
  if (/^\d{4}$/.test(normalized)) return new Date(Number(normalized), 0, 1);
  if (/^\d{4}-\d{2}$/.test(normalized)) {
    const [y, m] = normalized.split('-').map(Number);
    return new Date(y, m - 1, 1);
  }
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

const statusOrder = { reading: 0, planned: 1, finished: 2, paused: 3 };

const statusConfig = {
  reading: {
    label: 'Currently Reading',
    color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    badgeColor: 'bg-emerald-500 text-white',
    icon: <FaBookOpen size={11} />
  },
  finished: {
    label: 'Finished',
    color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
    badgeColor: 'bg-blue-500 text-white',
    icon: <FaCheck size={11} />
  },
  planned: {
    label: 'Planned',
    color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
    badgeColor: 'bg-amber-500 text-white',
    icon: <FaClock size={11} />
  },
  paused: {
    label: 'Paused',
    color: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/20',
    badgeColor: 'bg-zinc-500 text-white',
    icon: <FaPause size={11} />
  }
};

const Bookshelf = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [selectedBook, setSelectedBook] = useState(null);
  const [copiedQuoteIndex, setCopiedQuoteIndex] = useState(null);

  // Close modal on escape
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') setSelectedBook(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleCopyQuote = (quote, index) => {
    navigator.clipboard.writeText(quote);
    setCopiedQuoteIndex(index);
    setTimeout(() => setCopiedQuoteIndex(null), 2000);
  };

  const enriched = useMemo(() => {
    return (books || []).map(source => {
      const stats = getBookStats(source);
      const pubDate = parsePublicationDate(source.publicationDate);
      const computedStatus = source.status || (stats.percent >= 100 ? 'finished' : stats.percent > 0 ? 'reading' : 'planned');
      return { book: source, stats, pubDate, status: computedStatus };
    });
  }, []);

  // Compute overall vault analytics
  const analytics = useMemo(() => {
    const totalBooks = enriched.length;
    const readingBooks = enriched.filter(e => e.status === 'reading').length;
    const finishedBooks = enriched.filter(e => e.status === 'finished').length;
    const totalPagesRead = enriched.reduce((sum, e) => sum + e.stats.uniquePagesRead, 0);
    const totalPages = enriched.reduce((sum, e) => sum + e.stats.totalPages, 0);
    const avgProgress = totalPages > 0 ? Math.round((totalPagesRead / totalPages) * 100) : 0;

    return { totalBooks, readingBooks, finishedBooks, totalPagesRead, totalPages, avgProgress };
  }, [enriched]);

  const filteredAndSorted = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let working = enriched;
    if (statusFilter !== 'all') {
      working = working.filter(entry => entry.status === statusFilter);
    }
    if (q) {
      working = working.filter(entry => {
        const { title, author, description } = entry.book;
        return (
          (title || '').toLowerCase().includes(q) ||
          (author || '').toLowerCase().includes(q) ||
          (description || '').toLowerCase().includes(q)
        );
      });
    }

    const compare = (a, b) => {
      if (sortBy === 'title') {
        return a.book.title.localeCompare(b.book.title);
      }
      if (sortBy === 'author') {
        return a.book.author.localeCompare(b.book.author);
      }
      if (sortBy === 'date') {
        const at = a.pubDate ? a.pubDate.getTime() : 0;
        const bt = b.pubDate ? b.pubDate.getTime() : 0;
        return at - bt;
      }
      if (sortBy === 'progress') {
        return a.stats.percent - b.stats.percent;
      }
      if (sortBy === 'status') {
        return (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      }
      return 0;
    };

    const sorted = working.slice().sort(compare);
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [enriched, searchQuery, statusFilter, sortBy, sortDir]);

  const statusCounts = useMemo(() => {
    return {
      all: enriched.length,
      reading: enriched.filter(e => e.status === 'reading').length,
      finished: enriched.filter(e => e.status === 'finished').length,
      planned: enriched.filter(e => e.status === 'planned').length,
      paused: enriched.filter(e => e.status === 'paused').length,
    };
  }, [enriched]);

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 max-w-7xl mx-auto space-y-12 select-text">
      
      {/* ========================================================= */}
      {/* 1. HEADER & INTRO */}
      {/* ========================================================= */}
      <div className="space-y-4 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary dark:text-dark-primary border border-primary/20 backdrop-blur-md">
          <FaBookmark className="w-3 h-3" />
          <span>Engineering Reading Vault & Notes</span>
        </div>
        
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
          My Bookshelf
        </h1>
        <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
          A curated collection of foundational computer science literature, distributed systems architectures, and practical engineering guides.
        </p>
      </div>

      {/* ========================================================= */}
      {/* 2. M3 READING ANALYTICS SUMMARY */}
      {/* ========================================================= */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric 1: Currently Reading */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <FaBookOpen size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Active Reading</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.readingBooks} Books</div>
          </div>
        </div>

        {/* Metric 2: Pages Read */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <FaLayerGroup size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Pages Logged</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.totalPagesRead.toLocaleString()} pgs</div>
          </div>
        </div>

        {/* Metric 3: Vault Completion */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
            <FaChartPie size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Vault Progress</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.avgProgress}%</div>
          </div>
        </div>

        {/* Metric 4: Total Titles */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <FaBookmark size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Total Volumes</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.totalBooks} Titles</div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 3. M3 SEARCH & FILTER CONTROLS */}
      {/* ========================================================= */}
      <div className="space-y-6">
        {/* Full-width Search Bar */}
        <div className="relative flex items-center">
          <FaSearch className="absolute left-5 text-zinc-400 pointer-events-none" size={16} />
          <input
            type="text"
            placeholder="Search books by title, author, or keywords..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-4 rounded-full bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:focus:ring-dark-primary/40 backdrop-blur-md shadow-sm text-sm sm:text-base transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-5 p-1 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              aria-label="Clear search"
            >
              <FaTimes size={14} />
            </button>
          )}
        </div>

        {/* Filter Chips & View Switcher Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          
          {/* Status Filter Chips */}
          <div className="flex items-center flex-wrap gap-2">
            {[
              { id: 'all', label: 'All', count: statusCounts.all },
              { id: 'reading', label: 'Reading', count: statusCounts.reading },
              { id: 'finished', label: 'Finished', count: statusCounts.finished },
              { id: 'planned', label: 'Planned', count: statusCounts.planned },
              { id: 'paused', label: 'Paused', count: statusCounts.paused },
            ].map(tab => {
              const isSelected = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 border ${
                    isSelected
                      ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 border-zinc-900 dark:border-white shadow-sm'
                      : 'bg-white/60 dark:bg-zinc-900/60 text-zinc-600 dark:text-zinc-400 border-zinc-200/80 dark:border-zinc-800/80 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    isSelected ? 'bg-white/20 text-white dark:bg-zinc-900/20 dark:text-zinc-900' : 'bg-zinc-200/60 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sort Selector & Segmented View Toggle */}
          <div className="flex items-center gap-3">
            {/* Sort Select */}
            <select
              value={`${sortBy}:${sortDir}`}
              onChange={e => {
                const [by, dir] = e.target.value.split(':');
                setSortBy(by);
                setSortDir(dir);
              }}
              className="text-xs sm:text-sm font-medium px-4 py-2 rounded-full bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-primary/40 backdrop-blur-md cursor-pointer"
            >
              <option value="date:desc">Newest Published</option>
              <option value="date:asc">Oldest Published</option>
              <option value="progress:desc">Progress (High → Low)</option>
              <option value="progress:asc">Progress (Low → High)</option>
              <option value="title:asc">Title (A → Z)</option>
              <option value="author:asc">Author (A → Z)</option>
            </select>

            {/* M3 Segmented View Toggle */}
            <div className="p-1 rounded-full bg-zinc-200/60 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 flex items-center">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-full transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-zinc-900 text-primary dark:text-dark-primary shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                }`}
                title="Grid View"
              >
                <FaThLarge size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-full transition-all ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-zinc-900 text-primary dark:text-dark-primary shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                }`}
                title="List View"
              >
                <FaList size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 4. BOOKS DISPLAY (GRID VS LIST) */}
      {/* ========================================================= */}
      {filteredAndSorted.length === 0 ? (
        <div className="text-center py-20 bg-white/40 dark:bg-zinc-900/40 rounded-3xl border border-zinc-200/60 dark:border-zinc-800/60">
          <p className="text-zinc-500 text-base">No books found matching your current search or filter.</p>
          <button
            onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
            className="mt-4 text-xs font-semibold text-primary dark:text-dark-primary hover:underline"
          >
            Reset Filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        /* GRID VIEW */
        <motion.div 
          layout
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          <AnimatePresence>
            {filteredAndSorted.map(({ book, stats, pubDate, status }) => {
              const statusCfg = statusConfig[status] || statusConfig.planned;
              const year = pubDate ? pubDate.getFullYear() : '—';
              return (
                <motion.div
                  key={book.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setSelectedBook({ book, stats, pubDate, status })}
                  className="group cursor-pointer rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-4 flex flex-col justify-between hover:shadow-xl hover:border-zinc-400 dark:hover:border-zinc-600 transition-all duration-300"
                >
                  <div className="space-y-4">
                    {/* Book Cover with Aspect Ratio */}
                    <div className="relative aspect-[2/3] w-full rounded-2xl overflow-hidden shadow-md bg-gradient-to-tr from-zinc-200 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900">
                      {book.cover ? (
                        <img 
                          src={book.cover} 
                          alt={book.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl font-extrabold text-zinc-400">
                          {book.title.charAt(0)}
                        </div>
                      )}

                      {/* Status Tag Overlay */}
                      <div className="absolute top-2.5 right-2.5">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md ${statusCfg.badgeColor}`}>
                          {statusCfg.icon}
                          <span className="capitalize">{status}</span>
                        </span>
                      </div>
                    </div>

                    {/* Book Meta */}
                    <div className="space-y-1.5">
                      <h3 className="font-bold text-base text-zinc-900 dark:text-white leading-tight line-clamp-2 group-hover:text-primary dark:group-hover:text-dark-primary transition-colors">
                        {book.title}
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                        <FaUserEdit size={12} className="shrink-0 text-zinc-400" />
                        <span className="truncate">{book.author}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-500 font-mono">
                        <FaCalendarAlt size={11} className="shrink-0" />
                        <span>{year}</span>
                      </div>
                    </div>
                  </div>

                  {/* Reading Progress Indicator */}
                  <div className="pt-4 mt-4 border-t border-zinc-200/60 dark:border-zinc-800/60 space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-mono text-zinc-600 dark:text-zinc-400">
                      <span>{stats.uniquePagesRead} / {stats.totalPages} pgs</span>
                      <span className="font-bold text-zinc-900 dark:text-white">{stats.percent}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                      <div 
                        className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500"
                        style={{ width: `${stats.percent}%` }}
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      ) : (
        /* LIST / TABLE VIEW */
        <motion.div layout className="space-y-3">
          <AnimatePresence>
            {filteredAndSorted.map(({ book, stats, pubDate, status }) => {
              const statusCfg = statusConfig[status] || statusConfig.planned;
              const year = pubDate ? pubDate.getFullYear() : '—';
              return (
                <motion.div
                  key={book.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  onClick={() => setSelectedBook({ book, stats, pubDate, status })}
                  className="group cursor-pointer rounded-2xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:shadow-lg hover:border-zinc-400 dark:hover:border-zinc-600 transition-all"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-16 rounded-lg overflow-hidden bg-zinc-200 dark:bg-zinc-800 shrink-0 shadow-sm">
                      {book.cover ? (
                        <img src={book.cover} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-bold text-zinc-400">
                          {book.title.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-base text-zinc-900 dark:text-white truncate group-hover:text-primary dark:group-hover:text-dark-primary transition-colors">
                        {book.title}
                      </h3>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                        {book.author} • {year}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 w-full sm:w-auto justify-between sm:justify-end shrink-0">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full border ${statusCfg.color}`}>
                      {statusCfg.icon}
                      <span className="capitalize">{status}</span>
                    </span>

                    <div className="w-32 space-y-1">
                      <div className="flex justify-between text-[11px] font-mono text-zinc-500">
                        <span>{stats.uniquePagesRead} pgs</span>
                        <span>{stats.percent}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500"
                          style={{ width: `${stats.percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ========================================================= */}
      {/* 5. M3 DETAILED INSPECTION MODAL */}
      {/* ========================================================= */}
      <AnimatePresence>
        {selectedBook && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/40 backdrop-blur-md"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedBook(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl p-6 sm:p-8 space-y-6"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedBook(null)}
                className="absolute top-5 right-5 p-2.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                aria-label="Close dialog"
              >
                <FaTimes size={14} />
              </button>

              {/* Book Header info */}
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="w-32 aspect-[2/3] rounded-2xl overflow-hidden bg-zinc-200 dark:bg-zinc-800 shrink-0 shadow-lg">
                  {selectedBook.book.cover ? (
                    <img src={selectedBook.book.cover} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-zinc-400">
                      {selectedBook.book.title.charAt(0)}
                    </div>
                  )}
                </div>

                <div className="space-y-3 min-w-0">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${statusConfig[selectedBook.status]?.color || ''}`}>
                    {statusConfig[selectedBook.status]?.icon}
                    <span className="capitalize">{selectedBook.status}</span>
                  </span>

                  <h2 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 dark:text-white leading-tight">
                    {selectedBook.book.title}
                  </h2>
                  
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                    By <span className="text-zinc-900 dark:text-white font-semibold">{selectedBook.book.author}</span>
                    {selectedBook.pubDate && ` • ${selectedBook.pubDate.getFullYear()}`}
                  </p>

                  <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed pt-1">
                    {selectedBook.book.description}
                  </p>
                </div>
              </div>

              {/* Reading Progress Block */}
              <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80 space-y-2">
                <div className="flex justify-between items-center text-xs font-mono">
                  <span className="text-zinc-600 dark:text-zinc-400">Progress Logged</span>
                  <span className="font-bold text-zinc-900 dark:text-white">
                    {selectedBook.stats.uniquePagesRead} of {selectedBook.stats.totalPages} Pages ({selectedBook.stats.percent}%)
                  </span>
                </div>
                <div className="h-2 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
                  <div 
                    className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500"
                    style={{ width: `${selectedBook.stats.percent}%` }}
                  />
                </div>
              </div>

              {/* Personal Engineering Review */}
              {selectedBook.book.review && (
                <div className="space-y-2">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-bold">
                    Key Takeaways & Review
                  </h4>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50/60 dark:bg-zinc-800/30 p-4 rounded-2xl border border-zinc-200/60 dark:border-zinc-700/60 leading-relaxed italic">
                    "{selectedBook.book.review}"
                  </p>
                </div>
              )}

              {/* Key Quotes Callouts */}
              {Array.isArray(selectedBook.book.quotes) && selectedBook.book.quotes.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-bold">
                    Key Architectural Quotes
                  </h4>
                  <div className="space-y-2.5">
                    {selectedBook.book.quotes.map((quote, qIdx) => (
                      <div 
                        key={qIdx}
                        className="group flex items-start justify-between gap-3 p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200/60 dark:border-zinc-700/60"
                      >
                        <div className="flex gap-2.5">
                          <FaQuoteLeft className="text-primary dark:text-dark-primary shrink-0 mt-1" size={12} />
                          <span className="text-xs sm:text-sm text-zinc-800 dark:text-zinc-200 leading-relaxed">
                            {quote}
                          </span>
                        </div>
                        <button
                          onClick={() => handleCopyQuote(quote, qIdx)}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 dark:hover:text-white shrink-0"
                          title="Copy quote"
                        >
                          {copiedQuoteIndex === qIdx ? <FaCheck className="text-emerald-500" size={12} /> : <FaCopy size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Bookshelf;
